/**
 * Subscription Access Control
 * 
 * Centralized logic for determining feature access based on subscription offers.
 * 
 * Flow:
 * 1. Get user's active recurring subscription
 * 2. Extract package ID from subscription
misc * 3. Call /api/v1/packages/{id}/applicable to get package details (includes offers with service subcategories)
 * 4. Extract service subcategory codes directly from offers (no need to call /api/v1/offers/{id})
 * 5. Map offer sub-category codes to features
 * 6. Save applicable package data to Redux for use throughout the app
 */

import billingService from "../services/billingService";
import { getUser } from "../services/localService";
import logger from "./logger";
import { findActiveRecurringSubscription } from "./subscriptionUtils";

/**
 * Feature codes mapped to offer sub-category codes
 * These are the offer sub-category codes that enable each feature
 */
const FEATURE_OFFER_CODE_MAP = {
  BOOKING_BY_CUSTOMER: ["BOOKING_BY_CUSTOMER"],
  CUSTOMER_MANAGEMENT: ["CUSTOMER_MANAGEMENT"],
  CALENDAR_VIEW_OF_BOOKINGS: ["CALENDAR_VIEW_OF_BOOKINGS"],
  TEAM_MESSAGING: ["TEAM_MESSAGING"],
  CUSTOMER_MESSAGING: ["CUSTOMER_MESSAGING"],
  BOOKING_FOR_CUSTOMER: ["BOOKING_FOR_CUSTOMER"],
  MULTI_LOCATION: ["MULTI_LOCATION"],
  WHITE_LABELING: ["WHITE_LABELING"],
};

/**
 * Cache for feature access to avoid repeated API calls
 */
let featureAccessCache = {
  enabledFeatures: new Set(),
  lastUpdated: null,
  cacheDuration: 5 * 60 * 1000, // 5 minutes
};

/**
 * Get active recurring subscription for the current user's organization
 * @returns {Promise<Object|null>} Active subscription or null
 */
export const getActiveRecurringSubscription = async () => {
  try {
    const currentUser = getUser();
    const organizationId = currentUser?.organizationId || currentUser?.organization?.id;

    if (!organizationId) {
      logger.warn("No organization ID found for current user");
      return null;
    }

    // Get organization subscriptions
    const response = await billingService.getOrganizationSubscriptions({
      organizationId,
      orderBy: "subscriptionDate:desc",
      status: "USABLE",
      limit: 50,
    });

    if (!response?.data?.data || !Array.isArray(response.data.data)) {
      logger.warn("No subscriptions found or invalid response format");
      return null;
    }

    const subscriptions = response.data.data;
    const activeSubscription = findActiveRecurringSubscription(subscriptions);

    if (activeSubscription) {
      logger.log("Found active recurring subscription:", activeSubscription);
      return activeSubscription;
    }

    logger.log("No active recurring subscription found");
    return null;
  } catch (error) {
    logger.error("Error fetching active recurring subscription:", error);
    return null;
  }
};

/**
 * Get package details with offers
 * Calls /api/v1/packages/{id}/applicable to get package details
 * @param {string} packageId - Package ID
 * @param {Function} dispatch - Optional Redux dispatch function to save package to Redux
 * @returns {Promise<Object|null>} Package details with offers or null
 */
export const getPackageDetails = async (packageId, dispatch = null) => {
  try {
    if (!packageId) {
      logger.warn("No package ID provided");
      return null;
    }

    logger.log(`📦 Calling /api/v1/packages/${packageId}/applicable to get package details...`);
    logger.log(`📦 Calling /api/v1/packages/${packageId}/applicable to get package details...`);

    const response = await billingService.getApplicablePackageById(packageId);

    logger.log("📦 Full package response:", response);
    logger.log("📦 Full package response:", response);

    if (response?.data?.data) {
      const packageData = response.data.data;
      logger.log("✅ Package details retrieved:", packageData);
      logger.log("✅ Package details retrieved:", packageData);
      logger.log(`📋 Package has ${packageData.offers?.length || packageData.offerIds?.length || 0} offers`);
      logger.log(`📋 Package has ${packageData.offers?.length || packageData.offerIds?.length || 0} offers`);

      if (packageData.offers && Array.isArray(packageData.offers)) {
        logger.log(`📋 Offers array:`, packageData.offers);
        logger.log(`📋 Offers array:`, packageData.offers);
        logger.log(`📋 First offer structure:`, packageData.offers[0]);
        logger.log(`📋 First offer structure:`, packageData.offers[0]);
      }

      // Save to Redux if dispatch is provided
      if (dispatch) {
        try {
          const { setApplicablePackage } = await import("../redux/actions/subscription");
          dispatch(setApplicablePackage(packageData));
          logger.log("✅ Saved applicable package to Redux");
          logger.log("✅ Saved applicable package to Redux");
        } catch (error) {
          logger.warn("⚠️ Failed to save applicable package to Redux:", error);
          logger.warn("⚠️ Failed to save applicable package to Redux:", error);
        }
      }

      return packageData;
    }

    logger.warn("⚠️ Package details not found or invalid response. Response structure:", response);
    logger.warn("⚠️ Package details not found or invalid response. Response structure:", response);
    return null;
  } catch (error) {
    logger.error("Error fetching package details:", error);
    logger.error("Error fetching package details:", error);
    return null;
  }
};

/**
 * Get offer details and extract sub-category code
 * @deprecated This function is no longer needed. The applicable package response includes offers with service subcategories.
 * Use the offers directly from the applicable package response instead.
 * Calls /api/v1/offers/{id} to get offer details
 * @param {number} offerId - Offer ID
 * @returns {Promise<string|null>} Offer sub-category code or null
 */
export const getOfferSubCategoryCode = async offerId => {
  try {
    if (!offerId) {
      logger.warn("No offer ID provided");
      return null;
    }

    logger.log(`🎁 Calling /api/v1/offers/${offerId} to get offer details...`);
    logger.log(`🎁 Calling /api/v1/offers/${offerId} to get offer details...`);

    const response = await billingService.getOfferById(offerId);

    if (response?.data?.data) {
      const offer = response.data.data;
      logger.log(`📦 Full offer response for ID ${offerId}:`, offer);
      logger.log(`📦 Full offer response for ID ${offerId}:`, offer);

      // Extract sub-category code from offer
      // The code is in serviceSubcategory.code
      const subCategoryCode = offer.serviceSubcategory?.code || offer.serviceSubcategoryCode || null;

      if (subCategoryCode) {
        logger.log(`✅ Offer ${offerId} sub-category code:`, subCategoryCode);
        logger.log(`✅ Offer ${offerId} sub-category code:`, subCategoryCode);
        return subCategoryCode;
      }

      logger.warn(`⚠️ No sub-category code found for offer ${offerId}. Offer structure:`, {
        hasServiceSubcategory: !!offer.serviceSubcategory,
        serviceSubcategory: offer.serviceSubcategory,
        serviceSubcategoryCode: offer.serviceSubcategoryCode,
      });
      logger.warn(`⚠️ No sub-category code found for offer ${offerId}. Offer structure:`, {
        hasServiceSubcategory: !!offer.serviceSubcategory,
        serviceSubcategory: offer.serviceSubcategory,
        serviceSubcategoryCode: offer.serviceSubcategoryCode,
      });
      return null;
    }

    logger.warn("Offer details not found or invalid response");
    return null;
  } catch (error) {
    logger.error(`Error fetching offer ${offerId} details:`, error);
    logger.error(`Error fetching offer ${offerId} details:`, error);
    return null;
  }
};

/**
 * Extract active recurring subscription from subscriptions array
 * @param {Array} subscriptions - Array of subscriptions
 * @returns {Object|null} Active recurring subscription or null
 */
export const extractActiveRecurringSubscription = subscriptions => {
  if (!subscriptions || !Array.isArray(subscriptions) || subscriptions.length === 0) {
    return null;
  }

  return findActiveRecurringSubscription(subscriptions) || null;
};

/**
 * Fetch and cache enabled features from subscription
 * @param {boolean} forceRefresh - Force refresh cache
 * @param {Object|null} providedSubscription - Optional: Use provided subscription instead of fetching
 * @param {Function} dispatch - Optional Redux dispatch function to save package to Redux
 * @returns {Promise<Set<string>>} Set of enabled feature codes
 */
export const fetchEnabledFeatures = async (forceRefresh = false, providedSubscription = null, dispatch = null) => {
  try {
    // Check cache first
    if (
      !forceRefresh &&
      featureAccessCache.lastUpdated &&
      Date.now() - featureAccessCache.lastUpdated < featureAccessCache.cacheDuration
    ) {
      logger.log("Using cached feature access");
      return featureAccessCache.enabledFeatures;
    }

    logger.log("🔐 Fetching enabled features from subscription...");
    logger.log("🔐 Fetching enabled features from subscription...");

    // Use provided subscription or get active subscription
    let subscription = providedSubscription;
    if (!subscription) {
      logger.log("No subscription provided, fetching active recurring subscription...");
      subscription = await getActiveRecurringSubscription();
    }

    if (!subscription) {
      logger.warn("No active subscription found, all features disabled");
      logger.warn("No active subscription found, all features disabled");
      featureAccessCache.enabledFeatures = new Set();
      featureAccessCache.lastUpdated = Date.now();
      return featureAccessCache.enabledFeatures;
    }

    logger.log("✅ Active recurring subscription found:", subscription);
    logger.log("✅ Active recurring subscription found:", subscription);

    const packageId = subscription.packageId || subscription.servicePackage?.id;

    if (!packageId) {
      logger.warn("No package ID found in subscription");
      logger.warn("No package ID found in subscription");
      featureAccessCache.enabledFeatures = new Set();
      featureAccessCache.lastUpdated = Date.now();
      return featureAccessCache.enabledFeatures;
    }

    logger.log(`📦 Package ID found: ${packageId}`);
    logger.log(`📦 Package ID found: ${packageId}`);

    // Get package details (includes offers with service subcategories) - Calls /api/v1/packages/{id}/applicable
    // This will also save to Redux if dispatch is provided
    logger.log(`📞 Calling /api/v1/packages/${packageId}/applicable...`);
    logger.log(`📞 Calling /api/v1/packages/${packageId}/applicable...`);
    const packageDetails = await getPackageDetails(packageId, dispatch);

    if (!packageDetails) {
      logger.warn("Package details not found");
      featureAccessCache.enabledFeatures = new Set();
      featureAccessCache.lastUpdated = Date.now();
      return featureAccessCache.enabledFeatures;
    }

    // Extract service subcategory codes directly from offers
    // The applicable package response includes offers with their serviceSubcategory objects
    logger.log("🔍 Extracting service subcategory codes directly from offers...");
    logger.log("🔍 Extracting service subcategory codes directly from offers...");

    const offers = packageDetails.offers || [];
    logger.log(`📋 Found ${offers.length} offers in package`);
    logger.log(`📋 Found ${offers.length} offers in package`);

    if (!Array.isArray(offers) || offers.length === 0) {
      logger.warn("⚠️ No offers found in package. Package details:", packageDetails);
      logger.warn("⚠️ No offers found in package. Package details:", packageDetails);
      featureAccessCache.enabledFeatures = new Set();
      featureAccessCache.lastUpdated = Date.now();
      return featureAccessCache.enabledFeatures;
    }

    // Extract subcategory codes directly from offers (no need to call /api/v1/offers/{id})
    const offerCodes = new Set();
    offers.forEach((offer, index) => {
      try {
        // Extract subcategory code from offer.serviceSubcategory.code
        const subCategoryCode = offer.serviceSubcategory?.code || null;

        if (subCategoryCode) {
          offerCodes.add(subCategoryCode);
          logger.log(
            `✅ [${index + 1}/${offers.length}] Added subcategory code: ${subCategoryCode} from offer ID: ${offer.id}`
          );
          logger.log(
            `✅ [${index + 1}/${offers.length}] Added subcategory code: ${subCategoryCode} from offer ID: ${offer.id}`
          );
        } else {
          logger.warn(`⚠️ [${index + 1}/${offers.length}] No subcategory code found for offer ID: ${offer.id}`);
          logger.warn(`⚠️ [${index + 1}/${offers.length}] No subcategory code found for offer ID: ${offer.id}`);
        }
      } catch (error) {
        logger.error(`❌ Error processing offer at index ${index}:`, error);
        logger.error(`❌ Error processing offer at index ${index}:`, error);
      }
    });

    logger.log(`✅ Extracted ${offerCodes.size} unique subcategory codes from offers`);
    logger.log(`✅ Extracted ${offerCodes.size} unique subcategory codes from offers`);
    logger.log("Offer sub-category codes:", Array.from(offerCodes));
    logger.log("Offer sub-category codes:", Array.from(offerCodes));

    // Map offer codes to features
    const enabledFeatures = new Set();
    Object.keys(FEATURE_OFFER_CODE_MAP).forEach(feature => {
      const requiredCodes = FEATURE_OFFER_CODE_MAP[feature];
      const hasFeature = requiredCodes.some(code => offerCodes.has(code));

      if (hasFeature) {
        enabledFeatures.add(feature);
        logger.log(`Feature enabled: ${feature}`);
        logger.log(`Feature enabled: ${feature}`);
      }
    });

    // Update cache
    featureAccessCache.enabledFeatures = enabledFeatures;
    featureAccessCache.lastUpdated = Date.now();

    logger.log("Enabled features:", Array.from(enabledFeatures));
    logger.log("Enabled features:", Array.from(enabledFeatures));
    return enabledFeatures;
  } catch (error) {
    logger.error("Error fetching enabled features:", error);
    logger.error("Error fetching enabled features:", error);
    // Return empty set on error (fail closed - no features enabled)
    featureAccessCache.enabledFeatures = new Set();
    featureAccessCache.lastUpdated = Date.now();
    return featureAccessCache.enabledFeatures;
  }
};

/**
 * Check if a specific feature is enabled
 * @param {string} featureCode - Feature code to check
 * @param {boolean} forceRefresh - Force refresh cache
 * @returns {Promise<boolean>} True if feature is enabled
 */
export const isFeatureEnabled = async (featureCode, forceRefresh = false) => {
  const enabledFeatures = await fetchEnabledFeatures(forceRefresh);
  return enabledFeatures.has(featureCode);
};

/**
 * Feature access check functions for each feature
 * These should be used in host screens to conditionally enable/disable features
 */

export const canBookByCustomer = async (forceRefresh = false) => {
  return isFeatureEnabled("BOOKING_BY_CUSTOMER", forceRefresh);
};

export const canManageCustomers = async (forceRefresh = false) => {
  return isFeatureEnabled("CUSTOMER_MANAGEMENT", forceRefresh);
};

export const canViewCalendarBookings = async (forceRefresh = false) => {
  return isFeatureEnabled("CALENDAR_VIEW_OF_BOOKINGS", forceRefresh);
};

export const canMessageTeam = async (forceRefresh = false) => {
  return isFeatureEnabled("TEAM_MESSAGING", forceRefresh);
};

export const canMessageCustomers = async (forceRefresh = false) => {
  return isFeatureEnabled("CUSTOMER_MESSAGING", forceRefresh);
};

export const canBookForCustomer = async (forceRefresh = false) => {
  return isFeatureEnabled("BOOKING_FOR_CUSTOMER", forceRefresh);
};

export const canUseMultiLocation = async (forceRefresh = false) => {
  return isFeatureEnabled("MULTI_LOCATION", forceRefresh);
};

export const canUseWhiteLabeling = async (forceRefresh = false) => {
  return isFeatureEnabled("WHITE_LABELING", forceRefresh);
};

/**
 * Get all enabled features as an object
 * @param {boolean} forceRefresh - Force refresh cache
 * @returns {Promise<Object>} Object with feature flags
 */
export const getAllFeatureFlags = async (forceRefresh = false) => {
  const enabledFeatures = await fetchEnabledFeatures(forceRefresh);

  return {
    BOOKING_BY_CUSTOMER: enabledFeatures.has("BOOKING_BY_CUSTOMER"),
    CUSTOMER_MANAGEMENT: enabledFeatures.has("CUSTOMER_MANAGEMENT"),
    CALENDAR_VIEW_OF_BOOKINGS: enabledFeatures.has("CALENDAR_VIEW_OF_BOOKINGS"),
    TEAM_MESSAGING: enabledFeatures.has("TEAM_MESSAGING"),
    CUSTOMER_MESSAGING: enabledFeatures.has("CUSTOMER_MESSAGING"),
    BOOKING_FOR_CUSTOMER: enabledFeatures.has("BOOKING_FOR_CUSTOMER"),
    MULTI_LOCATION: enabledFeatures.has("MULTI_LOCATION"),
    WHITE_LABELING: enabledFeatures.has("WHITE_LABELING"),
  };
};

/**
 * Clear the feature access cache
 */
export const clearFeatureCache = () => {
  featureAccessCache = {
    enabledFeatures: new Set(),
    lastUpdated: null,
    cacheDuration: 5 * 60 * 1000,
  };
  logger.log("Feature access cache cleared");
};
